import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { fetchDashboardData, logoutOfDashboard } from "../utils/dashboardApi";

const COLORS = {
  secondary: "#091B3A", primary: "#B5D3E5", mid: "#A5C0CE",
  light: "#E6F3FC", warn: "#C0392B", good: "#2E7D32",
};
const PIE_COLORS = [COLORS.secondary, COLORS.primary, COLORS.mid, "#6E90A8", "#3E5C74", "#D7E7EF"];

// ---------- i18n (dashboard-only — separate from the public site's language) ----------
const DASH_LANG_KEY = "kmc_dashboard_lang";
const dict = {
  EN: {
    title: "Analytics Dashboard",
    logout: "Logout",
    last7: "Last 7 days", last28: "Last 28 days", last90: "Last 90 days",
    vsPrevious: "vs previous period",
    tabs: { overview: "Overview", audience: "Audience", acquisition: "Acquisition", content: "Content & Behavior", conversions: "Conversions" },
    kpi: { activeUsers: "Active Users", sessions: "Sessions", pageViews: "Page Views", avgDuration: "Avg. Session Duration", engagementRate: "Engagement Rate", bounceRate: "Bounce Rate" },
    computed: { title: "Calculated KPIs", pagesPerSession: "Pages / Session", conversionRate: "Conversion Rate", conversionSub: "contact form submits ÷ sessions", ctaCTR: "CTA Click-Through Rate", ctaSub: "CTA clicks ÷ sessions" },
    panels: {
      trend: "Active Users & Sessions — Daily Trend", topPages: "Top Pages", allEvents: "Events (all types)",
      device: "Device", newReturning: "New vs Returning Visitors", browser: "Browser", os: "Operating System",
      language: "Language Preference", languageNote: "Counted from language toggle clicks, not the default language shown on first visit",
      country: "Top Countries", city: "Top Cities", traffic: "Traffic Source / Medium", channel: "Channel Grouping",
      landing: "Top Landing Pages", hourly: "Traffic by Hour of Day", weekday: "Traffic by Day of Week",
      section: "Section Views", sectionNote: "Count of times each section was seen — average time needs a Custom Metric (not set up yet)",
      scroll: "Scroll Depth Distribution", scrollNote: "Cumulative — reaching 100% is also counted in 25/50/75%",
      cta: "CTA / Link Clicks", ctaFallback: "No CTA data yet — if you just created the cta_label custom dimension, past events won't backfill",
      contactTrend: "Contact Form Submissions — Daily", contactFallback: "No contact form submissions in this period",
    },
    table: { source: "Source", medium: "Medium", sessions: "Sessions", channel: "Channel", page: "Page" },
    empty: "No data available",
    notReady: (e) => `Not available yet: ${e}`,
  },
  TH: {
    title: "แดชบอร์ดวิเคราะห์ข้อมูล",
    logout: "ออกจากระบบ",
    last7: "7 วันล่าสุด", last28: "28 วันล่าสุด", last90: "90 วันล่าสุด",
    vsPrevious: "เทียบกับช่วงก่อนหน้า",
    tabs: { overview: "ภาพรวม", audience: "ผู้ชม", acquisition: "ที่มาผู้เข้าชม", content: "เนื้อหา & พฤติกรรม", conversions: "การติดต่อกลับ" },
    kpi: { activeUsers: "ผู้ใช้งาน", sessions: "เซสชัน", pageViews: "การเข้าชมหน้า", avgDuration: "เวลาเฉลี่ยต่อเซสชัน", engagementRate: "อัตราการมีส่วนร่วม", bounceRate: "อัตราตีกลับ" },
    computed: { title: "KPI ที่คำนวณให้", pagesPerSession: "หน้าเว็บ / เซสชัน", conversionRate: "อัตราการติดต่อกลับ", conversionSub: "จำนวนส่งฟอร์ม ÷ เซสชัน", ctaCTR: "อัตราคลิก CTA", ctaSub: "จำนวนคลิก CTA ÷ เซสชัน" },
    panels: {
      trend: "ผู้ใช้งาน & เซสชัน รายวัน", topPages: "หน้าที่มีคนเข้าชมมากสุด", allEvents: "Events ทั้งหมด",
      device: "อุปกรณ์", newReturning: "ผู้ใช้ใหม่ vs กลับมาซ้ำ", browser: "เบราว์เซอร์", os: "ระบบปฏิบัติการ",
      language: "ภาษาที่เลือกใช้", languageNote: "นับจากตอนกดสลับภาษา ไม่ใช่ภาษาเริ่มต้นที่เห็นตอนเข้าเว็บครั้งแรก",
      country: "ประเทศที่เข้าชมมากสุด", city: "เมืองที่เข้าชมมากสุด", traffic: "แหล่งที่มา / Medium", channel: "กลุ่มช่องทาง",
      landing: "หน้าแรกที่เข้ามา (Landing Page)", hourly: "ช่วงเวลาที่มีคนเข้าเว็บมากสุด", weekday: "วันที่มีคนเข้าเว็บมากสุด",
      section: "การมองเห็นแต่ละ Section", sectionNote: "นับจำนวนครั้งที่เห็น — ถ้าอยากได้เวลาเฉลี่ยต้องสร้าง Custom Metric เพิ่ม (ยังไม่ได้ทำ)",
      scroll: "การกระจายตัวของ Scroll Depth", scrollNote: "เป็นค่าสะสม — เลื่อนถึง 100% จะถูกนับรวมใน 25/50/75% ด้วย",
      cta: "การคลิกปุ่ม / ลิงก์ (CTA)", ctaFallback: "ยังไม่มีข้อมูล CTA — ถ้าเพิ่งสร้าง Custom Dimension cta_label ข้อมูลเก่าจะไม่ย้อนกลับมา",
      contactTrend: "จำนวนฟอร์มติดต่อที่ส่งเข้ามา รายวัน", contactFallback: "ยังไม่มีคนส่งฟอร์มติดต่อในช่วงนี้",
    },
    table: { source: "แหล่งที่มา", medium: "Medium", sessions: "เซสชัน", channel: "ช่องทาง", page: "หน้า" },
    empty: "ยังไม่มีข้อมูล",
    notReady: (e) => `ยังดึงข้อมูลนี้ไม่ได้: ${e}`,
  },
};

function getInitialDashLang() {
  try {
    const saved = localStorage.getItem(DASH_LANG_KEY);
    if (saved === "EN" || saved === "TH") return saved;
  } catch { /* ignore */ }
  return "EN";
}

// ---------- small building blocks ----------

function StatCard({ label, value, changePct, sub }) {
  const hasChange = typeof changePct === "number" && !Number.isNaN(changePct);
  const up = hasChange && changePct > 0;
  const down = hasChange && changePct < 0;
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "18px 20px", boxShadow: "0 4px 16px rgba(9,27,58,0.08)", flex: "1 1 170px" }}>
      <p style={{ fontSize: "0.78rem", color: "#667", marginBottom: 6 }}>{label}</p>
      <p style={{ fontSize: "1.5rem", fontWeight: 700, color: COLORS.secondary, margin: 0 }}>{value}</p>
      {hasChange && (
        <p style={{ fontSize: "0.75rem", marginTop: 4, color: up ? COLORS.good : down ? COLORS.warn : "#98A" }}>
          {up ? "▲" : down ? "▼" : "–"} {Math.abs(changePct)}%
        </p>
      )}
      {sub && <p style={{ fontSize: "0.72rem", color: "#98A", marginTop: 4 }}>{sub}</p>}
    </div>
  );
}

function Panel({ title, note, children, minHeight = 280, span = 1 }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "20px", boxShadow: "0 4px 16px rgba(9,27,58,0.08)", marginBottom: 20, gridColumn: span === 2 ? "span 2" : "span 1" }}>
      <h3 style={{ fontSize: "0.92rem", color: COLORS.secondary, marginBottom: 2 }}>{title}</h3>
      {note && <p style={{ fontSize: "0.72rem", color: "#98A", marginBottom: 12 }}>{note}</p>}
      <div style={{ width: "100%", height: minHeight, marginTop: note ? 0 : 14 }}>{children}</div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#98A", fontSize: "0.82rem", textAlign: "center", padding: 20 }}>
      {message}
    </div>
  );
}

function SectionBody({ section, fallbackMessage, t, children }) {
  if (!section?.available) {
    return <EmptyState message={section?.error ? t.notReady(section.error) : fallbackMessage || t.empty} />;
  }
  if (!section.data || section.data.length === 0) {
    return <EmptyState message={fallbackMessage || t.empty} />;
  }
  return children;
}

const percentLabel = (p) => `${p}%`;

// ---------- main component ----------

export default function Dashboard() {
  const [days, setDays] = useState(28);
  const [tab, setTab] = useState("overview");
  const [lang, setLang] = useState(getInitialDashLang);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const t = dict[lang];

  useEffect(() => {
    try { localStorage.setItem(DASH_LANG_KEY, lang); } catch { /* ignore */ }
  }, [lang]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchDashboardData(days);
      setData(result);
    } catch (err) {
      if (err.unauthorized) { navigate("/internal/login"); return; }
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [days, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => {
    await logoutOfDashboard();
    navigate("/internal/login");
  };

  const formatSeconds = (sec) => {
    const n = Number(sec) || 0;
    const m = Math.floor(n / 60);
    const s = Math.round(n % 60);
    return `${m}m ${s}s`;
  };
  const formatPercent1 = (v) => {
    const n = Number(v);
    return `${(Number.isFinite(n) ? n * 100 : 0).toFixed(1)}%`;
  };

  const TABS = [
    { id: "overview", label: t.tabs.overview },
    { id: "audience", label: t.tabs.audience },
    { id: "acquisition", label: t.tabs.acquisition },
    { id: "content", label: t.tabs.content },
    { id: "conversions", label: t.tabs.conversions },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#F5F8FA", padding: "32px 24px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <h1 style={{ fontSize: "1.4rem", color: COLORS.secondary, margin: 0 }}>{t.title}</h1>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              onClick={() => setLang((l) => (l === "EN" ? "TH" : "EN"))}
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #DCE3E8", background: "#fff", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600 }}
            >
              {lang === "EN" ? "TH" : "EN"}
            </button>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #DCE3E8" }}>
              <option value={7}>{t.last7}</option>
              <option value={28}>{t.last28}</option>
              <option value={90}>{t.last90}</option>
            </select>
            <button onClick={handleLogout} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #DCE3E8", background: "#fff", cursor: "pointer", fontSize: "0.85rem" }}>
              {t.logout}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 22, flexWrap: "wrap" }}>
          {TABS.map((tb) => (
            <button key={tb.id} onClick={() => setTab(tb.id)} style={{
              padding: "8px 16px", borderRadius: 999, border: "1px solid #DCE3E8",
              background: tab === tb.id ? COLORS.secondary : "#fff", color: tab === tb.id ? "#fff" : COLORS.secondary,
              fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
            }}>{tb.label}</button>
          ))}
        </div>

        {loading && <p>Loading...</p>}
        {error && <p style={{ color: COLORS.warn }}>{error}</p>}

        {data && !loading && (
          <>
            {/* ============ OVERVIEW ============ */}
            {tab === "overview" && (
              <>
                {!data.summary.available && (
                  <div style={{
                    background: "#FDECEA", border: "1px solid #F5C6C0", color: COLORS.warn,
                    borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: "0.82rem",
                  }}>
                    <strong>Summary query failed:</strong> {data.summary.error || "unknown error"}
                  </div>
                )}
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 10 }}>
                  <StatCard label={t.kpi.activeUsers} value={data.summary.activeUsers} changePct={data.summary.activeUsersChange} />
                  <StatCard label={t.kpi.sessions} value={data.summary.sessions} changePct={data.summary.sessionsChange} />
                  <StatCard label={t.kpi.pageViews} value={data.summary.pageViews} changePct={data.summary.pageViewsChange} />
                  <StatCard label={t.kpi.avgDuration} value={formatSeconds(data.summary.avgSessionDurationSec)} changePct={data.summary.avgSessionDurationSecChange} />
                  <StatCard label={t.kpi.engagementRate} value={formatPercent1(data.summary.engagementRate)} changePct={data.summary.engagementRateChange} />
                  <StatCard label={t.kpi.bounceRate} value={formatPercent1(data.summary.bounceRate)} changePct={data.summary.bounceRateChange} />
                </div>
                <p style={{ fontSize: "0.72rem", color: "#98A", marginBottom: 20 }}>▲▼ {t.vsPrevious}</p>

                {/* Computed KPIs */}
                <Panel title={t.computed.title} minHeight={100}>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", height: "100%", alignItems: "center" }}>
                    <StatCard label={t.computed.pagesPerSession} value={data.kpis.pagesPerSession} />
                    <StatCard label={t.computed.conversionRate} value={`${data.kpis.conversionRate}%`} sub={t.computed.conversionSub} />
                    <StatCard label={t.computed.ctaCTR} value={`${data.kpis.ctaClickThroughRate}%`} sub={t.computed.ctaSub} />
                  </div>
                </Panel>

                <Panel title={t.panels.trend} span={2}>
                  <SectionBody section={data.dailyTrend} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.dailyTrend?.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip /><Legend />
                        <Line type="monotone" dataKey="activeUsers" name={t.kpi.activeUsers} stroke={COLORS.secondary} strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="sessions" name={t.kpi.sessions} stroke={COLORS.mid} strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <Panel title={t.panels.topPages}>
                    <SectionBody section={data.topPages} t={t}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.topPages?.data} layout="vertical" margin={{ left: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="path" tick={{ fontSize: 10 }} width={150} />
                          <Tooltip /><Bar dataKey="views" fill={COLORS.secondary} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </SectionBody>
                  </Panel>

                  <Panel title={t.panels.allEvents}>
                    <SectionBody section={data.events} t={t}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.events?.data} layout="vertical" margin={{ left: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={150} />
                          <Tooltip /><Bar dataKey="count" fill={COLORS.mid} radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </SectionBody>
                  </Panel>
                </div>
              </>
            )}

            {/* ============ AUDIENCE ============ */}
            {tab === "audience" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <Panel title={t.panels.device}>
                  <SectionBody section={data.deviceBreakdown} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.deviceBreakdown?.data} dataKey="users" nameKey="device" outerRadius={90} label>
                          {(data.deviceBreakdown?.data || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip /><Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.newReturning}>
                  <SectionBody section={data.newVsReturning} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.newVsReturning?.data} dataKey="users" nameKey="type" outerRadius={90} label>
                          {(data.newVsReturning?.data || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip /><Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.browser}>
                  <SectionBody section={data.browserBreakdown} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.browserBreakdown?.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis dataKey="browser" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip /><Bar dataKey="users" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.os}>
                  <SectionBody section={data.osBreakdown} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.osBreakdown?.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis dataKey="os" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip /><Bar dataKey="users" fill={COLORS.mid} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.language} note={t.panels.languageNote}>
                  <SectionBody section={data.languageSwitch} fallbackMessage={t.empty} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.languageSwitch?.data} dataKey="count" nameKey="language" outerRadius={90} label>
                          {(data.languageSwitch?.data || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip /><Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.country}>
                  <SectionBody section={data.countryBreakdown} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.countryBreakdown?.data} layout="vertical" margin={{ left: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="country" tick={{ fontSize: 10 }} width={100} />
                        <Tooltip /><Bar dataKey="users" fill={COLORS.secondary} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.city} span={2}>
                  <SectionBody section={data.cityBreakdown} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.cityBreakdown?.data} layout="vertical" margin={{ left: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="city" tick={{ fontSize: 10 }} width={100} />
                        <Tooltip formatter={(v, n, p) => [`${v}`, p.payload.country]} />
                        <Bar dataKey="users" fill={COLORS.mid} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>
              </div>
            )}

            {/* ============ ACQUISITION ============ */}
            {tab === "acquisition" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <Panel title={t.panels.channel}>
                  <SectionBody section={data.channelGrouping} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.channelGrouping?.data} layout="vertical" margin={{ left: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="channel" tick={{ fontSize: 10 }} width={110} />
                        <Tooltip /><Bar dataKey="sessions" fill={COLORS.secondary} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.landing}>
                  <SectionBody section={data.landingPages} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.landingPages?.data} layout="vertical" margin={{ left: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="page" tick={{ fontSize: 10 }} width={140} />
                        <Tooltip /><Bar dataKey="sessions" fill={COLORS.mid} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.traffic} span={2} minHeight={220}>
                  <SectionBody section={data.trafficSource} t={t}>
                    <div style={{ overflowX: "auto", height: "100%" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                        <thead>
                          <tr style={{ textAlign: "left", color: "#667", borderBottom: "1px solid #EEF2F5" }}>
                            <th style={{ padding: "8px 6px" }}>{t.table.source}</th>
                            <th style={{ padding: "8px 6px" }}>{t.table.medium}</th>
                            <th style={{ padding: "8px 6px" }}>{t.table.sessions}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data.trafficSource?.data || []).map((row, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #F5F8FA" }}>
                              <td style={{ padding: "8px 6px" }}>{row.source}</td>
                              <td style={{ padding: "8px 6px" }}>{row.medium}</td>
                              <td style={{ padding: "8px 6px" }}>{row.sessions}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.hourly}>
                  <SectionBody section={data.hourlyPattern} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.hourlyPattern?.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={(h) => `${h}:00`} />
                        <Bar dataKey="users" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.weekday}>
                  <SectionBody section={data.weekdayPattern} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.weekdayPattern?.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip /><Bar dataKey="users" fill={COLORS.mid} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>
              </div>
            )}

            {/* ============ CONTENT & BEHAVIOR ============ */}
            {tab === "content" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <Panel title={t.panels.section} note={t.panels.sectionNote}>
                  <SectionBody section={data.sectionViews} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.sectionViews?.data} layout="vertical" margin={{ left: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="section" tick={{ fontSize: 10 }} width={110} />
                        <Tooltip /><Bar dataKey="views" fill={COLORS.secondary} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.scroll} note={t.panels.scrollNote}>
                  <SectionBody section={data.scrollDepth} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.scrollDepth?.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis dataKey="percent" tickFormatter={percentLabel} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={percentLabel} />
                        <Bar dataKey="count" fill={COLORS.mid} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>

                <Panel title={t.panels.cta} span={2}>
                  <SectionBody section={data.ctaClicks} fallbackMessage={t.panels.ctaFallback} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.ctaClicks?.data} layout="vertical" margin={{ left: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={140} />
                        <Tooltip /><Bar dataKey="count" fill={COLORS.secondary} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>
              </div>
            )}

            {/* ============ CONVERSIONS ============ */}
            {tab === "conversions" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 20 }}>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 4 }}>
                  <StatCard label={t.computed.conversionRate} value={`${data.kpis.conversionRate}%`} sub={`${data.kpis.contactSubmits} submits`} />
                  <StatCard label={t.computed.ctaCTR} value={`${data.kpis.ctaClickThroughRate}%`} sub={`${data.kpis.ctaClickCount} clicks`} />
                </div>
                <Panel title={t.panels.contactTrend} minHeight={300}>
                  <SectionBody section={data.contactTrend} fallbackMessage={t.panels.contactFallback} t={t}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.contactTrend?.data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F5" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Line type="monotone" dataKey="submits" name="Contact Submits" stroke={COLORS.secondary} strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </SectionBody>
                </Panel>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}